import WindowsEditionAvailability from "@/components/WindowsEditionAvailability";
import {
  ArrowRight,
  Cpu,
  Eye,
  LockKeyhole,
  Mic,
  Monitor,
  ShieldCheck,
  Video,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ProductWordmark from "@/components/ProductWordmark";
import DeviceStory from "@/components/DeviceStory";
import {
  downloadStatusLabel,
  secureMacStorefront,
} from "@/config/secureMacStorefront";
import {
  SECURESHIELD_AUDIT_DISCIPLINE,
  SECUREMAC_CHECK_CATEGORIES,
  SECUREMAC_CUSTOMER_PROMISE,
  SECUREMAC_HERO_STRAPLINE,
  SECUREMAC_PRIVACY_CONTROLS,
} from "@/config/secureMacMarketing";
import { SECUREMAC_PRO_TEASER } from "@/config/secureMacProMarketing";

const benefits = [
  {
    icon: Eye,
    eyebrow: "Real state",
    title: "Reads reality, not a guess.",
    body: "Eight automatic checks read macOS settings. Three firewall settings and Startup Security provide manual guidance when reliable readback is unavailable.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Guided fixes",
    title: "Guidance you can act on.",
    body: "SecureShield explains what needs attention and opens the relevant System Settings pane. Automatic checks can be checked again; manual guidance remains clearly labelled.",
  },
  {
    icon: Cpu,
    eyebrow: "Your control",
    title: "Your Mac, your call.",
    body: "Essential protections, helpful recommendations and optional hardening stay distinct. A deliberate choice is never treated as a failure — and SecureShield stops asking once you've told it that.",
  },
];

const categories = SECUREMAC_CHECK_CATEGORIES;

const faq = [
  [
    "What does SecureShield actually check?",
    "Eight automatic checks and four manual guidance items, including three firewall settings and Startup Security. Privacy icons open Camera, Microphone and Screen Recording Settings in one click.",
  ],
  [
    "Does SecureShield send anything over the network?",
    "Security checks run on your Mac. The App Store edition uses Apple for purchase and updates. No LightningFlow account or activation is required; optional purchase linking does not unlock features.",
  ],
  [
    "Will SecureShield ever turn off System Integrity Protection?",
    "No, and it never will. SecureShield only ever detects SIP's state and, if it's off, points you to macOS Recovery to restore it.",
  ],
  [
    "How does SecureShield fix things?",
    "SecureShield explains exactly what to change and opens the right System Settings pane, so you stay in control and macOS makes the change. Automatic checks can be checked again; manual items do not claim verified readback. Want one-click fixes? SecureShield Pro can change the essentials for you.",
  ],
  [
    "Does it conflict with Halo SuperVPN?",
    "No. SecureShield and Halo SuperVPN are independent — neither is required for the other to work, and SecureShield's checks were verified not to interfere with Halo's own network extension.",
  ],
  [
    "Which Macs does it support?",
    `${secureMacStorefront.requirements.os}, ${secureMacStorefront.requirements.chip}. Purchase and updates through the Mac App Store. Existing website purchases remain available in your LightningFlow account.`,
  ],
];

function DownloadAction({ dark = false }) {
  const live = secureMacStorefront.download.live;
  const content = (
    <>
      <span className="font-mono text-[9px] uppercase tracking-[.2em] opacity-70">
        {live ? "On the App Store" : "Launch availability"}
      </span>
      <span className="mt-2 block font-display text-xl font-bold">{downloadStatusLabel(secureMacStorefront)}</span>
      <span className="mt-2 block text-sm opacity-75">{secureMacStorefront.requirements.os} · {secureMacStorefront.requirements.chip}</span>
    </>
  );

  if (!live) {
    return (
      <div
        className={`h-full border px-5 py-6 text-left ${
          dark ? "border-white/20 bg-white/[.05] text-white" : "border-ink-100 bg-white text-ink-700"
        }`}
        aria-label={`SecureShield: ${downloadStatusLabel(secureMacStorefront)}`}
        aria-disabled="true"
      >
        {content}
      </div>
    );
  }

  return (
    <a
      className={`block h-full border px-5 py-6 text-left transition-colors ${
        dark
          ? "border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
          : "border-ink-900 bg-ink-900 text-white hover:border-electric hover:bg-electric"
      }`}
      href={secureMacStorefront.download.url}
      data-testid="securemac-download"
    >
      {content}
    </a>
  );
}

function SectionHeading({ eyebrow, title, body, light = false }) {
  return (
    <div className="max-w-3xl">
      <div className={`lf-eyebrow ${light ? "text-cyan-200" : ""}`}>{eyebrow}</div>
      <h2 className="mt-4 font-display text-4xl font-black leading-[1.02] md:text-5xl">{title}</h2>
      {body ? (
        <p className={`mt-5 text-lg leading-relaxed ${light ? "text-slate-200" : "text-ink-600"}`}>{body}</p>
      ) : null}
    </div>
  );
}

function AuditDiscipline() {
  const { steps } = SECURESHIELD_AUDIT_DISCIPLINE;
  return (
    <div className="border border-ink-900 bg-white" role="group" aria-labelledby="securemac-discipline-title">
      <div className="grid gap-5 border-b border-ink-900 px-5 py-6 md:grid-cols-[1fr_auto] md:items-end md:px-7">
        <div>
          <p className="lf-eyebrow">{SECURESHIELD_AUDIT_DISCIPLINE.method}</p>
          <h3 id="securemac-discipline-title" className="mt-2 font-display text-2xl font-black">{SECURESHIELD_AUDIT_DISCIPLINE.title}</h3>
        </div>
        <strong className="font-display text-3xl font-black text-electric md:text-4xl">{SECURESHIELD_AUDIT_DISCIPLINE.claim}</strong>
      </div>
      <ol className="grid divide-y divide-ink-100 px-5 py-2 md:grid-cols-4 md:divide-x md:divide-y-0 md:px-7">
        {steps.map((step, index) => (
          <li key={step.label} className="py-5 md:px-5 md:py-7">
            <span className="font-mono text-xs text-electric">0{index + 1}</span>
            <div className="mt-2 font-display text-lg font-bold">{step.label}</div>
            <p className="mt-2 text-sm leading-relaxed text-ink-600">{step.body}</p>
          </li>
        ))}
      </ol>
      <p className="border-t border-ink-100 px-5 py-4 text-sm leading-relaxed text-ink-600 md:px-7">
        {SECURESHIELD_AUDIT_DISCIPLINE.test_note}
      </p>
    </div>
  );
}

function TrustStrip() {
  const priceLabel = "US$4.99 one-time";
  const items = [
    ["Supported platforms", `${secureMacStorefront.requirements.os} · ${secureMacStorefront.requirements.chip}`],
    ["What it checks", "8 automatic checks / 4 manual guides"],
    ["License", `${priceLabel} · ${secureMacStorefront.license_model}`],
  ];

  return (
    <section aria-label="SecureShield availability and license summary" className="border-y border-ink-100 bg-white">
      <div className="lf-container grid gap-px bg-ink-100 md:grid-cols-3">
        {items.map(([label, value]) => (
          <div key={label} className="bg-white px-6 py-6">
            <div className="font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{label}</div>
            <div className="mt-2 text-sm font-semibold leading-relaxed text-ink-900">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SecureMacProductPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: secureMacStorefront.product_name,
    applicationCategory: "SecurityApplication",
    operatingSystem: "macOS",
    description: "SecureShield reads your Mac's real security settings, shows you what needs attention and guides you to the fix, with eight automatic checks and four manual guidance items.",
    ...(secureMacStorefront.pricing_published
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: "4.99",
            availability: secureMacStorefront.download.live
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          },
        }
      : {}),
  };

  return (
    <div className="bg-white text-ink-900">
      <SEO
        title="SecureShield — Know Your Mac's Real Security State."
        description={SECUREMAC_CUSTOMER_PROMISE.description}
        canonicalPath="/secureshield"
        image="/secureshield/secure-shield-official-share-c9b52ec1.png"
        structuredData={structuredData}
      />

      <section className="lf-product-hero relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
        <div className="lf-container relative grid gap-12 py-16 md:py-24 lg:grid-cols-[1.15fr_.85fr] lg:items-center">
          <div className="max-w-3xl">
            <ProductWordmark
              product="securemac"
              size="hero"
              loading="eager"
              testId="securemac-hero-wordmark"
            />
            <div className="lf-eyebrow text-cyan-200">{SECUREMAC_HERO_STRAPLINE}</div>
            <h1 className="mt-5 font-display text-4xl font-black leading-[.94] tracking-[-.04em] md:text-6xl">
              {SECUREMAC_CUSTOMER_PROMISE.headline}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
              {SECUREMAC_CUSTOMER_PROMISE.description}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
                href="#plans"
                data-testid="securemac-cta-get"
              >
                Get SecureShield <ArrowRight className="h-4 w-4" />
              </a>
              <a
                className="lf-btn border border-white/40 text-white hover:border-white"
                href="#checks"
                data-testid="securemac-cta-checks"
              >
                See what it checks
              </a>
            </div>
          </div>
          <aside className="border border-white/15 bg-black/30 p-6 md:p-8" aria-label="SecureShield at a glance">
            <img src="/secureshield/secure-shield-logo-a114f451.png" alt="" width="1308" height="1202" className="mb-5 h-20 w-20 object-contain" aria-hidden="true" />
            <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">At a glance</div>
            <dl className="mt-6 space-y-5">
              <div>
                <dt className="text-sm text-slate-400">Checks</dt>
                <dd className="mt-1 font-display text-3xl font-black">8 automatic · 4 guided</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-400">Discipline</dt>
                <dd className="mt-1 font-display text-2xl font-bold">Read · fix · re-check</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-400">Distribution</dt>
                <dd className="mt-1 font-display text-2xl font-bold">Mac App Store</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-400">License</dt>
                <dd className="mt-1 font-display text-2xl font-bold">US$4.99 once</dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>

      <TrustStrip />

      <section id="product-preview" className="lf-editorial-section scroll-mt-24 border-b border-ink-100 bg-ink-50/60" aria-label="How SecureShield works">
        <div className="lf-container">
          <SectionHeading
            eyebrow="Inside SecureShield"
            title="See the checks on your Mac."
            body="SecureShield reads macOS settings and shows what passed, what needs attention, and where to review a setting."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-3 lg:items-stretch">
            {[
              { step: "01", title: "Check what matters", body: "Eight automatic checks and four manual guidance items.", line: "Security state" },
              { step: "02", title: "See what needs attention", body: "A clear explanation and the relevant System Settings path for each check.", line: "Guided review" },
              { step: "03", title: "Verify the change", body: "Automatic checks can be checked again. Manual guidance never claims automatic verification.", line: "Honest status" },
            ].map((item) => (
              <article key={item.step} className="relative min-h-[17rem] overflow-hidden border border-ink-200 bg-[#061a31] p-7 text-white md:p-9">
                <div aria-hidden="true" className="absolute -right-14 -top-16 h-48 w-48 rounded-full border border-cyan-300/20 shadow-[0_0_90px_10px_rgba(34,211,238,.15)]" />
                <div className="relative flex h-full flex-col">
                  <span className="font-mono text-[11px] tracking-[.3em] text-cyan-200">{item.step} / SECURESHIELD</span>
                  <h3 className="mt-10 font-display text-2xl font-bold">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate-300">{item.body}</p>
                  <span className="mt-auto pt-9 font-mono text-[10px] uppercase tracking-[.18em] text-emerald-300">{item.line}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-editorial-section">
        <div className="lf-container grid gap-14 lg:grid-cols-[.68fr_1.32fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow="Why SecureShield"
              title="Built around one rule: never fake a security state."
              body="A companion to Halo SuperVPN from the same team. Built for the Mac you already own — not another subscription to manage."
            />
          </div>
          <div className="mt-10 divide-y divide-ink-200 border-y border-ink-200">
            {benefits.map(({ icon: Icon, eyebrow, title, body }, index) => (
              <article key={title} className="grid grid-cols-[2rem_1fr] gap-4 py-6">
                <span className="font-mono text-[10px] tracking-[.18em] text-electric">0{index + 1}</span>
                <div>
                  <Icon className="h-5 w-5 text-electric" strokeWidth={1.6} />
                  <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{eyebrow}</div>
                  <h3 className="mt-2 font-display text-2xl font-bold">{title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-600">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="checks" className="scroll-mt-24 border-y border-ink-100 bg-[#031124] text-white">
        <div className="lf-container py-20 md:py-28">
          <SectionHeading
            light
            eyebrow="What SecureShield checks"
            title="Eight automatic checks. Four manual guides."
            body="Essential protection, helpful recommendations and optional hardening each get an honest bar for what needs attention — so a deliberate choice is never treated as a failure."
          />
          <div className="mt-8 space-y-3">
            {categories.map(([number, title, body]) => (
              <div key={number} className="grid grid-cols-[44px_1fr] gap-4 border-t border-white/15 py-4">
                <span className="font-mono text-xs text-cyan-200">{number}</span>
                <div>
                  <h3 className="font-display text-xl font-bold">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-300">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="discipline" className="lf-section scroll-mt-24 border-y border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr] lg:items-end">
            <SectionHeading eyebrow="No cloud. No telemetry." title="Verified, not assumed." />
            <p className="max-w-2xl text-lg leading-relaxed text-ink-600">
              Automatic checks read supported macOS settings again. Firewall, Stealth Mode, Block Incoming Connections and Startup Security remain manual guidance, without verified-state claims.
            </p>
          </div>
          <div className="mt-12">
            <AuditDiscipline />
          </div>
        </div>
      </section>


      <section id="privacy-controls" className="scroll-mt-24 lf-editorial-section border-y border-ink-100 bg-white" data-testid="securemac-privacy-controls">
        <div className="lf-container grid gap-12 lg:grid-cols-[.72fr_1.28fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow={SECUREMAC_PRIVACY_CONTROLS.eyebrow}
              title={SECUREMAC_PRIVACY_CONTROLS.title}
              body={SECUREMAC_PRIVACY_CONTROLS.body}
            />
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-ink-500">
              {SECUREMAC_PRIVACY_CONTROLS.honesty}
            </p>
          </div>
          <div className="grid gap-px border border-ink-100 bg-ink-100 sm:grid-cols-3">
            {SECUREMAC_PRIVACY_CONTROLS.controls.map((control) => {
              const Icon = control.id === "camera" ? Video : control.id === "microphone" ? Mic : Monitor;
              return (
                <article key={control.id} className="bg-white p-6 md:p-7" data-testid={`securemac-privacy-${control.id}`}>
                  <Icon className="h-5 w-5 text-electric" strokeWidth={1.6} />
                  <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">One tap</div>
                  <h3 className="mt-2 font-display text-xl font-bold text-ink-900">{control.label}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-600">{control.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="plans" className="scroll-mt-24 bg-[#031124] text-white">
        <div className="lf-container py-16 md:py-20">
          <SectionHeading
            eyebrow="Get SecureShield"
            title="US$4.99. One-time purchase."
            body="Get SecureShield from the Mac App Store when available. One purchase, no subscription, and no LightningFlow activation."
            light
          />

          <div className="mt-10 grid gap-3 sm:max-w-md">
            <DownloadAction dark />
            <WindowsEditionAvailability product="secureshield" />
          </div>

          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
            Already bought SecureShield from this website? Your existing licence, download and activation remain available in your LightningFlow account. No repurchase is required.
          </p>
          <Link className="lf-link mt-3 inline-flex text-cyan-200" to="/account" data-testid="standard-existing-purchase">Manage an existing website purchase</Link>
        </div>

        <div className="border-y border-ink-200 bg-[#f3f3f5] text-ink-900">
          <div className="lf-container grid sm:grid-cols-3">
            {[
              { value: "US$4.99 once", label: "Price" },
              { value: "0", label: "Subscriptions required" },
              { value: "0", label: "Security-check results uploaded" },
            ].map((item) => (
              <article key={item.label} className="border-b border-ink-200 px-6 py-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 md:px-8">
                <div className="font-display text-3xl font-black tracking-[-.03em]">{item.value}</div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[.24em] text-ink-600">{item.label}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="border-y border-ink-100 bg-ink-900 text-white">
        <div className="lf-container grid gap-12 py-16 md:py-20 lg:grid-cols-[.72fr_1.28fr]">
          <div>
            <LockKeyhole className="h-7 w-7 text-cyan-300" />
            <h2 className="mt-5 font-display text-4xl font-black">Private by design.</h2>
            <p className="mt-5 leading-relaxed text-slate-200">
              Your security checks run on your Mac. The App Store edition needs no LightningFlow account or activation. Linking a purchase is optional. Your security-check results stay on your Mac.
            </p>
          </div>
          <div className="grid gap-px bg-white/10 sm:grid-cols-2">
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">Never collected</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Usage analytics</li>
                <li>Crash or diagnostic reports</li>
                <li>Your security check results</li>
                <li>Security-check results linked to an account</li>
              </ul>
            </article>
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">Security checks stay on your Mac</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Every check, every fix</li>
                <li>Every audit-verify cycle</li>
                <li>No network connection needed for checks</li>
                <li>No cloud service needed for checks</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <SectionHeading
            eyebrow="From the same team"
            title="Protects what you have. Protects what you send."
            body="SecureShield hardens the Mac you already own. Halo SuperVPN protects your connection when you're on it. Independent products, same LightningFlow standard for honest, verified security."
          />
          <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
            <article className="bg-white p-7 md:p-9">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Discover</div>
              <h3 className="mt-5 font-display text-3xl font-black">Halo SuperVPN</h3>
              <p className="mt-4 leading-relaxed text-ink-600">Private, high-performance protection powered by LightningFlow's QuantumCloak security feature.</p>
              <Link className="lf-btn-secondary mt-7" to="/halo" data-testid="securemac-related-halo">
                Discover Halo SuperVPN <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
            <article className="bg-white p-7 md:p-9">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">Discover</div>
              <h3 className="mt-5 font-display text-3xl font-black">Security &amp; Trust</h3>
              <p className="mt-4 leading-relaxed text-ink-600">How LightningFlow approaches trust boundaries, identity-aware controls and security framework alignment across the platform.</p>
              <Link className="lf-btn-secondary mt-7" to="/security" data-testid="securemac-related-security">
                Read Security &amp; Trust <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          </div>
        </div>
      </section>

      <DeviceStory product="securemac" />

      <section className="lf-section">
        <div className="lf-container grid gap-12 lg:grid-cols-[.65fr_1.35fr]">
          <SectionHeading eyebrow="Questions" title="Clear before you buy." />
          <div className="divide-y divide-ink-100 border border-ink-100 bg-white">
            {faq.map(([question, answer]) => (
              <details key={question} className="group p-6">
                <summary className="cursor-pointer list-none pr-8 font-display text-lg font-bold marker:hidden">{question}</summary>
                <p className="mt-4 max-w-3xl leading-relaxed text-ink-600">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-editorial-section lf-editorial-dark" data-testid="securemac-final-cta">
        <div className="lf-container">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
            <div>
              <p className="lf-eyebrow text-cyan-200">{SECUREMAC_PRO_TEASER.eyebrow}</p>
              <h2 className="mt-5 max-w-5xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">
                {SECUREMAC_PRO_TEASER.headline}
              </h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">{SECUREMAC_PRO_TEASER.body}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white" to="/secureshield-pro" data-testid="securemac-related-pro">
                Discover SecureShield Pro <ArrowRight className="h-4 w-4" />
              </Link>
              <Link className="lf-btn border border-white/40 text-white hover:border-white" to="/halo-fortress#compare" data-testid="securemac-compare-editions">Compare editions</Link>
              <Link className="lf-btn border border-white/40 text-white hover:border-white" to="/halo-fortress" data-testid="securemac-discover-fortress">Discover Fortress</Link>
              <a className="lf-btn border border-white/40 text-white hover:border-white" href="#plans">
                Get SecureShield
              </a>
            </div>
          </div>
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SECUREMAC_PRO_TEASER.highlights.map((item) => (
              <article key={item.title} className="border border-white/15 bg-white/[.04] p-6">
                <h3 className="font-display text-xl font-bold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
